<template>
  <div v-if="error.length < 1" class="c-login-error">{{ error }}</div>

  <div class="p-page-create-edit" v-else-if="!loading">
    <page-title
      marginBottom="0"
      subTitle="Add details of your event, then (if required) customise the listing by adding additonal information"
    >
      <span v-if="editMode">Edit Event</span>
      <span v-else>Create Event</span>
    </page-title>

    <section-block :headerOverflowHidden="true">
      <template slot="header">
        <form-field
          dynamic-width
          ghost
          large
          min-width="500px"
          v-model="event.title"
          v-validate="{ required: true }"
          name="title"
          placeholder="Add your event name here..."
          :error="errors.first('title')"
          is-inline
        ></form-field>
      </template>

      <template slot="actions" v-if="editMode">
        <base-button
          @click="onClickViewEvent"
          v-if="event.status === 'approved'"
          >View Event</base-button
        >
        <base-button
          primary="true"
          @click="onClickUpdate"
          iconBefore="edit"
          v-if="userCanAdd"
          :disabled="disabled"
          >{{
            event.status == 'rejected' ? 'Re-submit' : 'Update'
          }}</base-button
        >
        <base-button
          danger
          @click="onClickReject"
          v-if="event.status === 'pending' && userCanApprove"
          :disabled="disabled"
          >Reject</base-button
        >
        <base-button
          success
          @click="onClickApprove"
          v-if="event.status === 'pending' && userCanApprove"
          :disabled="disabled"
          >Approve</base-button
        >
      </template>
      <template slot="actions" v-else>
        <base-button
          primary="true"
          @click="onClickSubmit"
          v-if="userCanAdd"
          :disabled="disabled"
          >Submit</base-button
        >
      </template>

      <page-builder
        :pageId="event.id"
        @reviewDateUpdated="(newDate) => (articlereview_date = newDate)"
        :activeSbPanel="activeSbPanel"
        :load-pages="false"
      >
        <template slot="intro">
          <div class="c-pb__no-elements--landscape">
            <div class="c-pb__no-elements-text">
              <p>Ready to add content to your event?</p>
              <p>
                Select
                <strong>&lsquo;Customise Event&rsquo;</strong> tab in the right
                hand menu
              </p>
            </div>
            <inline-svg name="page-builder-landscape"></inline-svg>
          </div>
        </template>
        <template slot="preFields">
          <image-content-split
            :title="event.title"
            :large="activeSbPanel === 'settings'"
          >
            <div slot="image">
              <img
                src="/assets/IconEvent.png"
                width="95"
                height="94"
                alt="Add an event"
                title="Add an event"
              />
            </div>
            <div slot="content">
              <p class="m-0">
                <strong>Event Name:</strong>
                {{ event.title }}
              </p>
              <p class="m-0">
                <strong>Location:</strong>
                {{ locationString }}
              </p>
              <p class="m-0">
                <strong>Date &amp; Time:</strong>
                {{ ' ' }}
                <span v-if="dateString">{{ dateString }}</span>
              </p>
            </div>
          </image-content-split>

          <banner-upload
            name="default_image_id"
            :image="event.default_image"
            :docType="docType"
            :fileTypes="documentFileTypes"
            :resource="docType.singular"
            @uploaded="onFileUploaded"
            :bypassDuplicate="true"
            v-model="event.default_image"
            @handleBannerChange="handleBannerChange"
          >
            <p>Upload a default banner for your event here</p>
          </banner-upload>
        </template>
        <tabs slot="tabs">
          <tabs-item
            :active="activeSbPanel === 'settings'"
            @click="activeSbPanel = 'settings'"
            >Event Details</tabs-item
          >

          <tabs-item
            :active="activeSbPanel === 'elements'"
            @click="activeSbPanel = 'elements'"
            >Customise Event</tabs-item
          >
        </tabs>

        <div slot="sidebars">
          <div class="c-pb__sb-settings" v-show="activeSbPanel === 'settings'">
            <form-field
              label="Event Location"
              name="locations"
              helper-text="Please type the event location below"
              :required="true"
            >
              <form-multiselect
                :show-valid-tick="false"
                placeholder="Select Location"
                v-model="event.location"
                v-if="locations"
                v-validate="{ required: true }"
                :options="locations"
                :multiple="false"
                label="name"
                track-by="id"
                @input="updateLocation"
                data-vv-as="location"
                id="location"
                name="location"
                :taggable="true"
                :error="errors.first('location')"
                @tag="addLocation"
              ></form-multiselect>
              <p v-else>Loading</p>
            </form-field>

            <form-field
              label="Event Date"
              name="event_date"
              :helper-text="'Please enter the date of your event'"
            >
              <form-datepicker
                small
                v-model="date"
                name="start_date"
                format="DD / MM / YYYY"
                @input="onPublishDateInput"
                input-class="c-form__input"
                data-vv-as="start_date"
                :error="errors.first('start_date')"
                v-validate="
                  `required: true|date_format:yyyy-MM-dd|date_between:${$dayjs().format(
                    'YYYY-MM-DD'
                  )},${$dayjs()
                    .add(1, 'year')
                    .format('YYYY-MM-DD')},1`
                "
                :disabledDates="disabledDates"
              ></form-datepicker>
            </form-field>

            <form-field
              label="Event Time"
              name="event_time"
              :helper-text="'Please select from drop down list below'"
            >
              <div class="row">
                <div class="col-xs">
                  <time-picker
                    v-validate="{ required: true }"
                    v-model="start_time"
                    :value="start_time"
                    placeholder="Start"
                    name="start_time"
                    vvAs="start time"
                    :error="errors.first('start_time')"
                    @input="updateStartTime"
                  ></time-picker>
                </div>
                <div class="col-xs">
                  <time-picker
                    v-validate="{ required: true }"
                    v-model="end_time"
                    :value="end_time"
                    placeholder="End"
                    name="end_time"
                    vvAs="End time"
                    :error="errors.first('end_time')"
                    @input="updateEndTime"
                  ></time-picker>
                </div>
              </div>
            </form-field>

            <form-field
              label="Interest Groups"
              name="interest_groups"
              helper-text="Help users find your event by tagging related interest groups:"
            >
              <form-multiselect
                :show-valid-tick="true"
                v-model="event.interestgroups"
                :options="interestGroups"
                :multiple="true"
                label="name"
                track-by="name"
                :taggable="true"
              ></form-multiselect>
            </form-field>
          </div>
        </div>
      </page-builder>
    </section-block>
    <modal
      v-if="rejectModalOpen"
      class="ta-center"
      show-close-button
      @close="rejectModalOpen = false"
    >
      <h1 slot="header">Reason for Rejection</h1>
      <form method="post" action v-on:submit.prevent="rejectEvent">
        <form-field
          type="textarea"
          placeholder="Please specify a reason for rejection"
          v-model="rejectionReason"
          required
        />
        <base-button type="submit" :primary="true">Submit Reason</base-button>
      </form>
    </modal>
  </div>

  <loading v-else></loading>
</template>

<script>
/**
 * TODO: This is based on the page builder for content, but needs altering to
 * fit the needs of the news section.  Ideally the same 'could' be used for events
 */
import PageBuilder from '@/components/PageBuilder'
import { TimePicker, BannerUpload } from '@/components/forms'
import { ImageContentSplit } from '@/components/Containers'
import { mapState } from 'vuex'
import UserHasPermission from '@/mixins/UserHasPermission'
import { InlineLabel } from '@/components/elements'

export default {
  name: 'EventAddEdit',
  components: {
    PageBuilder,
    ImageContentSplit,
    BannerUpload,
    TimePicker,
    InlineLabel,
  },
  mixins: [UserHasPermission],
  beforeRouteLeave(to, from, next) {
    if (this.elementsHaveChanged && this.editMode) {
      this.$store.dispatch('createDialog', {
        messageText:
          'Any changes you made will be lost if you navigate away from this page, are you sure?',
        confirm: () => {
          next()
        },
      })
    } else {
      next()
    }
  },

  data() {
    return {
      loading: true,
      editMode: false,
      activeSbPanel: 'settings',
      docType: {
        single: 'event',
        plural: 'events',
      },
      documentFileTypes: ['png', 'gif', 'jpg', 'jpeg'],
      date: this.$dayjs().format('YYYY-MM-DD'),
      start_time: '',
      end_time: '',
      locationString: '',
      originalPageElements: {},
      rejectModalOpen: false,
      rejectionReason: '',
      disabled: true,
    }
  },
  computed: {
    ...mapState('Events', ['event', 'error']),
    ...mapState('Locations', ['locations']),
    ...mapState({
      interestGroups: (state) => state.InterestGroups.collection,
    }),
    userCanAdd() {
      return this.userHasPermission('add_edit_events')
    },

    userCanApprove() {
      return this.userHasPermission('publish_events')
    },
    disabledDates() {
      const yesterday = this.$dayjs()
        .subtract(1, 'day')
        .toDate()
      return { to: yesterday }
    },

    startDate() {
      const { date, start_time } = this

      if (date && start_time) {
        const dateFormat = this.$dayjs(date).format('YYYY-MM-DD')
        return `${dateFormat} ${start_time}`
      }
    },
    dateString() {
      const { date, start_time, end_time } = this

      if (date && start_time && end_time) {
        return `${this.$dayjs(`${date}`).format(
          'DD/MM/YY'
        )} ${start_time} - ${end_time}`
      }
    },
    duration() {
      const { date, start_time, end_time } = this
      if (date && start_time && end_time) {
        const date1 = this.$dayjs(`${date}T${start_time}`)
        const date2 = this.$dayjs(`${date}T${end_time}`)
        return date2.diff(date1, 'minute')
      }
    },
    // TODO: This has been copied from the pageElements
    elementsHaveChanged() {
      try {
        if (this.originalPageElements.length !== this.elementList.length) {
          return true
        }

        for (let i = this.originalPageElements.length; i--; ) {
          if (this.originalPageElements[i] !== this.elementList[i]) {
            return true
          }
        }
      } catch (err) {
        // console.log(err.message);
      }

      return false
    },
    elementList() {
      return this.event.elements
    },
  },
  watch: {
    startDate(newValue, oldValue) {
      this.event.start_date = newValue
    },
    duration(newValue, oldValue) {
      this.event.duration = newValue
    },
  },
  created() {
    this.loading = true
    // reset
    this.$store.commit('pageElements/setPageElements', [])
    this.$store.dispatch('Locations/list', { flat: true })

    const { id } = this.$route.params || null

    const { duplicate } = this.$route.query

    this.$store.commit('Events/RESET_EVENT')

    if (id) {
      this.editMode = true
      this.retrieveEvent()
    } else {
      this.loading = false
      this.disabled = false
    }
  },
  methods: {
    rejectEvent() {
      const vm = this
      // do the following, but post the rejection reason along with the id i guess
      this.$store
        .dispatch('Events/reject', {
          id: this.event.id,
          reason: this.rejectionReason,
        })
        .then((resp) => {
          this.rejectionReason = ''
          this.rejectModalOpen = false
          vm.$store.dispatch(
            'notifications/create',
            {
              body: `The event was rejected`,
              type: 'success',
            },
            { root: true }
          )
        })
    },
    onClickPublish() {
      // This needs to either onSave or onClickUpdate, depending on if we are in edit mode or not, and then publish

      if (this.editMode) {
        this.onClickUpdate(true)
      } else {
        this.onSave(true)
      }
    },

    onClickSubmit() {
      // here we need to just set the status to pending
      if (this.editMode) {
        this.onClickUpdate()
      } else {
        this.onSave()
      }
    },
    onSave() {
      const vm = this
      this.$validator.validate().then((result) => {
        if (!result) {
          vm.showValidationMessage = true
        } else {
          this.loading = true
          vm.showValidationMessage = false
          vm.event.elements = [...this.$store.state.pageElements.pageElements]
          //interest groups
          // interest groupe
          if (
            this.event.interestgroups &&
            this.event.interestgroups.length > 0
          ) {
            this.event.interestgroup_ids = this.event.interestgroups.map(
              (grp) => grp.id
            )
          }

          //then we only need certain fields
          const {
            user,
            user_favourite,
            website_url,
            self,
            modified_user,
            modified_time,
            favourite_count,
            default_image,
            created_user,
            created_time,
            id,
            location,
            interestgroups,
            interestgroup,
            ...payload
          } = this.event

          this.$store
            .dispatch('Events/create', payload)
            .then((page) => {
              vm.loading = false

              this.$store.commit('pageElements/setPageElements', [])
              this.$router.push({
                name: 'AdminEventsEdit',
                params: { id: page.data.data.event[0].id },
              })
            })
            .catch((error) => {
              vm.loading = false
            })
        }
      })
    },
    updateLocation(loc) {
      if (loc.id) {
        this.event.location_id = loc.id
        this.locationString = loc.name
      }
    },
    updateStartTime(val) {
      this.start_time = val
    },
    updateEndTime(val) {
      this.end_time = val
    },
    onFileUploaded(fileData) {
      this.event.default_image_id = fileData.id
    },
    handleBannerChange() {},
    addLocation(newLoc) {
      const locationInfo = {
        id: null,
        name: newLoc,
      }
      this.$store.dispatch('Locations/add', newLoc).then((resp) => {
        this.event.location_id = resp.id
        this.locationString = resp.name
      })
    },
    onPublishDateInput(val) {
      this.date = this.$dayjs(val).format('YYYY-MM-DD')
    },
    retrieveEvent(pageId, setDuplicate) {
      this.loading = true
      pageId = pageId || this.$route.params.id
      this.$store.dispatch('Events/read', pageId).then((resp) => {
        /**
         * Get the response from the promise
         */
        if (this.event.location) {
          this.locationString = this.event.location.name
        }
        // need to destructure the date
        this.date = this.$dayjs(this.event.start_date).format('YYYY-MM-DD')
        this.start_time = this.$dayjs(this.event.start_date).format('HH:mm')
        this.end_time = this.$dayjs(this.event.start_date)
          .add(this.event.duration, 'minute')
          .format('HH:mm')

        this.retrievePageElements(resp)
      })

      this.image_uploaded = true
    },

    retrievePageElements(page) {
      let elements = page ? page.elements : this.event.elements

      if (!elements) {
        elements = []
      }

      this.$store.commit('pageElements/setPageElements', elements)
      this.originalPageElements = elements ? elements.slice() : []

      this.loading = false
      this.disabled = false
    },
    onClickViewEvent() {
      const route = this.$router.resolve(`/schedule/event/${this.event.id}`)
      window.open(route.href, '_blank')
    },
    onClickUpdate() {
      const vm = this
      const { page, $store, event } = vm

      this.$validator.validate().then((result) => {
        if (!result) {
          this.showValidationMessage = true
        } else {
          const pageElments = $store.getters['pageElements/forServer']

          const editedPage = JSON.parse(JSON.stringify(event))

          // add elements to the current page.
          editedPage.elements = pageElments

          // interest groups
          if (
            editedPage.interestgroups &&
            editedPage.interestgroups.length > 0
          ) {
            editedPage.interestgroup_ids = editedPage.interestgroups.map(
              (grp) => grp.id
            )
          }

          //then we only need certain fields
          const {
            user,
            user_favourite,
            website_url,
            self,
            modified_user,
            modified_time,
            favourite_count,
            default_image,
            created_user,
            created_time,
            id,
            interestgroup,
            interestgroups,
            location,
            ...payload
          } = editedPage

          this.loading = true

          //depending on the articles current status, we need to either update or resubmit
          const action = 'Events/resubmit'

          this.$store
            .dispatch(action, {
              pageId: this.event.id,
              pageData: payload,
            })
            .then((response) => {
              this.$store.commit('pageElements/setPageElements', [])
              this.$store.commit('Events/GET_EVENT', [])
              this.retrieveEvent(this.event.id)
            })
            .catch((error) => {
              this.loading = false
              if (error.meta.duplicate_page) {
                this.duplicatePage = error.meta.duplicate_page
              }
            })
        }
      })
    },
    onClickApprove() {
      const vm = this
      this.$store.dispatch('Events/approve', this.event.id).then((page) => {
        vm.$store.dispatch(
          'notifications/create',
          {
            body: `The event was approved`,
            type: 'success',
          },
          { root: true }
        )
      })
    },

    onClickReject() {
      /**
       * For this, we need to give a reason as to why we are rejecting
       */
      this.rejectModalOpen = true
      return
    },
  },
}
</script>

<style lang="scss">
.p-page-create-edit {
  .c-section-block__header-main .c-form__group {
    margin: 0;
  }
}

/**
 * TODO: Should this be in the pagebuilder comp?
 **/
.c-pb__no-elements--landscape {
  position: relative;

  .c-inline-svg {
    display: none;

    @media screen and (min-width: 1024px) {
      display: inline;
    }
  }

  .c-inline-svg,
  .c-pb__no-elements-text {
    @media screen and (min-width: 1024px) {
      position: absolute;
    }
  }

  .c-pb__no-elements-text {
    text-align: center;
    @media screen and (min-width: 1024px) {
      text-align: left;
      left: 33%;
    }
    z-index: 5;
  }
}
</style>
